import { useState } from 'react'
import { Plus, Edit, Trash2, HelpCircle, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react'
import { useFAQs, useDeleteFAQ, useUpdateFAQ, useReorderFAQs } from '@/hooks/use-faq'
import type { TenantFaq } from '@/hooks/use-faq'
import FAQFormModal from './faq-form-modal'

export default function FAQPage() {
  const { data: faqs = [], isLoading } = useFAQs()
  const deleteFAQ = useDeleteFAQ()
  const updateFAQ = useUpdateFAQ()
  const reorderFAQs = useReorderFAQs()

  const [modalOpen, setModalOpen] = useState(false)
  const [editingFaq, setEditingFaq] = useState<TenantFaq | undefined>(undefined)

  function openCreate() {
    setEditingFaq(undefined)
    setModalOpen(true)
  }

  function openEdit(faq: TenantFaq) {
    setEditingFaq(faq)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingFaq(undefined)
  }

  function handleTogglePublished(faq: TenantFaq) {
    updateFAQ.mutate({ id: faq.id, is_published: !faq.is_published })
  }

  function handleDelete(faq: TenantFaq) {
    if (confirm(`Hapus FAQ "${faq.question}"?`)) {
      deleteFAQ.mutate(faq.id)
    }
  }

  function handleMoveUp(index: number) {
    if (index === 0) return
    const prev = faqs[index - 1]
    const curr = faqs[index]
    reorderFAQs.mutate([
      { id: curr.id, display_order: prev.display_order },
      { id: prev.id, display_order: curr.display_order },
    ])
  }

  function handleMoveDown(index: number) {
    if (index === faqs.length - 1) return
    const next = faqs[index + 1]
    const curr = faqs[index]
    reorderFAQs.mutate([
      { id: curr.id, display_order: next.display_order },
      { id: next.id, display_order: curr.display_order },
    ])
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat FAQ...</p>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">FAQ</h1>
          <p className="text-muted-foreground">
            Kelola pertanyaan yang sering diajukan fans
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Tambah FAQ
        </button>
      </div>

      {/* Empty state */}
      {faqs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <HelpCircle className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="font-medium">Belum ada FAQ</p>
          <p className="text-sm text-muted-foreground mt-1">
            Tambahkan pertanyaan yang sering diajukan fans Anda.
          </p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Tambah FAQ
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className={`rounded-xl border bg-card overflow-hidden ${!faq.is_published ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-3 px-4 py-3">
                {/* Reorder */}
                <div className="flex flex-col gap-0.5 pt-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0 || reorderFAQs.isPending}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    title="Naik"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === faqs.length - 1 || reorderFAQs.isPending}
                    className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    title="Turun"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium leading-snug">{faq.question}</p>
                    {faq.category && (
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {faq.category}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{faq.answer}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleTogglePublished(faq)}
                    disabled={updateFAQ.isPending}
                    className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    title={faq.is_published ? 'Sembunyikan' : 'Tampilkan'}
                  >
                    {faq.is_published ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => openEdit(faq)}
                    className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    title="Edit"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq)}
                    disabled={deleteFAQ.isPending}
                    className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    title="Hapus"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FAQFormModal open={modalOpen} onClose={closeModal} faq={editingFaq} />
    </div>
  )
}
